import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './InstagramGrid.module.scss';
import {
  DateFormatSetting,
  InstagramApiType,
  PostOrderUpdateDirection,
  PostQueuePosition,
  PostSource,
  PostStatus,
  SocialProfileFieldsFragment,
  TimeFormatSetting
} from '../../../models/graphql';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { IPhone } from './IPhone';
import { Select } from '../../../components/forms/Select';
import { UseFormReturn } from 'react-hook-form';
import { Slider } from './Slider';
import classNames from 'classnames';
import { GridMediaCard } from './GridMediaCard';
import { useDimensions } from '../../../hooks/useDimensions';
import { MandatoryChildren } from '../../../models/children-props';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { CardDragLayer } from './CardDragLayer';
import { EmptyPlaceholder } from '../../../components/containers/EmptyPlaceholder';
import instagramPreview from '../../../assets/images/instagramPreview.svg';
import { IconsEnum } from '../../../constants/icons';
import { useNavigate } from 'react-router-dom';
import { InstagramGridContext } from '../../../providers/InstagramGridProvider';
import { format } from 'date-fns';
import { useSettings } from '../../../hooks/useSettings';
import {
  SelectItem,
  SelectItemTemplate,
  SelectValueTemplate,
  SelectViewProps
} from '../../../components/forms/Select/Select.view';

import {
  SelectProfileItemTemplateView,
  SelectProfileTemplateViewProps
} from '../../Discover/FindContent/SelectTemplate/SelectTemplate.view';
import { SocialProfile } from '../../../components/socialProfiles/SocialProfile';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import { useOpenPostEditor } from '../../../hooks/useOpenPostEditor';
import { TopNavContext } from '../../../providers/TopNavProvider';
import { Loader } from '../../../components/containers/Loader';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { InstagramGridPostType } from '../../../models/post';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { BoxNotification } from '../../../components/containers/BoxNotification';

type ErrorConfig = {
  message: string;
  label: string;
  action: () => void;
};

export type InstagramGridViewProps = {
  igGridPosts: InstagramGridPostType[];
  instagramProfiles: SocialProfileFieldsFragment[];
  useFormInstance: UseFormReturn;
  profileSelectAction: FormElementAction<string>;
  refetchPosts: () => void;
  updatePostLocation: (postId: string, refPostId: string, direction: PostOrderUpdateDirection) => Promise<void>;
  movePostPosition: (postId: string, position: PostQueuePosition) => void;
  trashSinglePost: (postId: string) => void;
  getPostScheduleListItems: () => void;
  igApiError?: string;
};

const MAX_POSTS_IN_VIEW = 15;

const ConditionalWrapper = (props: MandatoryChildren & { isMobile: boolean }): JSX.Element => {
  const { loadingPosts } = useContext(InstagramGridContext);

  return props.isMobile ? (
    <>{loadingPosts ? <Loader variation={'dark'} size={'large'} type={'overlay'} /> : <>{props.children}</>}</>
  ) : (
    <IPhone>{props.children}</IPhone>
  );
};

// @TODO: after alpha, and when documented, this needs to use formatDateString from the useDateString hook
export type PreviewDateStringProps = {
  sliderValue: number;
  posts: InstagramGridPostType[];
};

export const PreviewDateString = ({ sliderValue, posts }: PreviewDateStringProps): JSX.Element => {
  const { selectedOrgSettings } = useSettings();
  const isOnlyPastPosts = posts.every(p => p.status !== PostStatus.ACTIVE && p.status !== PostStatus.DRAFT);

  if (sliderValue === 0 || posts.length === 0 || (isOnlyPastPosts && sliderValue <= posts.length)) {
    return <>How your feed looks now</>;
  }

  const dateFormatString =
    selectedOrgSettings?.dateFormat === DateFormatSetting.MONTH_DAY_YEAR ? 'EE MMM dd yyyy' : 'EE dd, MMM yyyy';
  const timeFormatString = selectedOrgSettings?.timeFormat === TimeFormatSetting.HOUR_12 ? 'h:mm a' : 'HH:mm';

  const preposition = sliderValue > posts.length ? 'after' : 'at';
  const date = new Date(posts[0].calculatedLocalDateTime);

  return (
    <>
      {`Preview of your feed ${preposition} `}
      {format(date, `${timeFormatString} ${dateFormatString}`)}
    </>
  );
};

export const InstagramGridView = (props: InstagramGridViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { state } = useContext(TopNavContext);
  const { loadingPage, loadingPosts, setLoadingPosts, selectedProfileId, sliderValue, setSliderValue } =
    useContext(InstagramGridContext);
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const ref = useRef(document.body);
  const { width } = useDimensions(ref);
  const { hasScrollbar } = useDimensions(scrollRef);
  const {
    useFormInstance,
    instagramProfiles,
    igGridPosts,
    profileSelectAction,
    updatePostLocation,
    movePostPosition,
    trashSinglePost
  } = props;

  const [grabbedPost, setGrabbedPost] = useState<InstagramGridPostType | undefined>(undefined);
  const [mutablePosts, setMutablePosts] = useState<InstagramGridPostType[]>([]);
  const { openNewPost } = useOpenPostEditor();

  const profileListItems = instagramProfiles.map(profile => {
    return { label: profile.displayName, value: profile.id };
  });

  const isMobile = useMemo(() => width < BreakpointsValuesEnum.medium, [width]);
  const itemCnt = useMemo(() => mutablePosts.length, [mutablePosts]);
  const isGraphApi = instagramProfiles.find(p => {
    return (
      p.id === selectedProfileId &&
      // We know this is an IG profile, but TS does not
      p.__typename === 'InstagramSocialProfile' &&
      (p.apiType === InstagramApiType.FACEBOOK_GRAPH || p.apiType === InstagramApiType.INSTAGRAM_GRAPH)
    );
  });

  const placeHolderCount = useMemo(() => {
    return !sliderValue ? 0 : sliderValue > itemCnt ? sliderValue - itemCnt : 0;
  }, [itemCnt, sliderValue]);

  const postIndexOffset = useMemo(() => {
    const defaultStart = 0;
    const startIndex = sliderValue && sliderValue <= itemCnt ? itemCnt - sliderValue : defaultStart;
    const placeholderCount = !sliderValue ? 0 : itemCnt > sliderValue ? 0 : sliderValue - itemCnt;
    let endIndex = startIndex + MAX_POSTS_IN_VIEW - placeholderCount;

    // Ensure endIndex does not exceed the length of mutablePosts
    endIndex = Math.min(endIndex, mutablePosts.length);

    return { start: startIndex, end: endIndex };
  }, [itemCnt, mutablePosts.length, sliderValue]);

  // This has dumbed down since inception, should we remove?
  const adjustedInViewPostIndex = useCallback(
    (postIndex: number) => {
      return postIndex + postIndexOffset.start;
    },
    [postIndexOffset]
  );

  const reorderPosts = useCallback(
    (dragId: string, hoverOverIndex: number | undefined): InstagramGridPostType[] => {
      // grab the index of the dragged post first
      const dragIndex = mutablePosts.findIndex(p => p.id === dragId);

      // if we don't have a hoverOverIndex or if the dragged post is the same as the hovered, return unmutated.
      if (typeof hoverOverIndex === 'undefined' || dragIndex === hoverOverIndex) return mutablePosts;

      // @TODO: Why are we doing this? This might be a bug because the passed in value is already adjusted!!!
      const actualHoverIndex = adjustedInViewPostIndex(hoverOverIndex);
      const dragPost = mutablePosts.find(p => p.id === dragId);

      if (!dragPost) return mutablePosts;

      // If post is hover self, return unchanged
      const hoverPost = mutablePosts[actualHoverIndex];
      if (
        !hoverPost ||
        hoverPost.socialProfile.id !== dragPost.socialProfile.id ||
        hoverPost.category?.id !== dragPost.category?.id
      ) {
        return mutablePosts;
      }

      const subQueue = mutablePosts.filter(
        post =>
          post.socialProfile.id === dragPost.socialProfile.id &&
          post.category?.id === dragPost.category?.id &&
          post.timing === 'QUEUE'
      );

      const dragSubIndex = subQueue.findIndex(post => post.id === dragId);
      const hoverSubIndex = subQueue.findIndex(post => post.id === hoverPost.id);

      subQueue.splice(dragSubIndex, 1);
      subQueue.splice(hoverSubIndex, 0, dragPost);

      let replacedIndex = 0;
      return mutablePosts.map(post => {
        if (
          post.socialProfile.id === dragPost.socialProfile.id &&
          post.category?.id === dragPost.category?.id &&
          post.timing === 'QUEUE'
        ) {
          return subQueue[replacedIndex++];
        }
        return post;
      });
    },
    [adjustedInViewPostIndex, mutablePosts]
  );

  const movePost = useCallback(
    (dragId: string, hoverOverIndex: number | undefined) => {
      const swappedPosts = reorderPosts(dragId, hoverOverIndex);

      const dragPost = mutablePosts.find(p => p.id === dragId);
      if (dragPost) {
        setGrabbedPost(dragPost);
      }

      setMutablePosts(swappedPosts);
    },
    [mutablePosts, reorderPosts]
  );

  const cancelMove = useCallback(() => {
    setGrabbedPost(undefined);
    setMutablePosts(() => igGridPosts.filter(p => p.status !== PostStatus.PUBLISHED));
  }, [igGridPosts]);

  const moveEnd = useCallback(
    (post: InstagramGridPostType, refIndex: number) => {
      const adjustRefIndex = adjustedInViewPostIndex(refIndex);
      const filteredPosts = mutablePosts.filter(
        p => p.category?.id === post.category?.id && p.socialProfile.id === post.socialProfile.id
      );

      const postSubIndex = filteredPosts.findIndex(p => p.id === post.id);
      const refPostSubIndex = filteredPosts.findIndex(p => p.id === mutablePosts[adjustRefIndex].id);

      // If we know this is a valid change, show the loading screen early
      if (postSubIndex !== refPostSubIndex) {
        setLoadingPosts(true);
      }

      const direction =
        postSubIndex > refPostSubIndex ? PostOrderUpdateDirection.BELOW : PostOrderUpdateDirection.ABOVE;

      const reorderedPosts = reorderPosts(post.id, adjustRefIndex);

      if (reorderedPosts !== mutablePosts) {
        setMutablePosts(reorderedPosts);
      }

      updatePostLocation(post.id, mutablePosts[adjustRefIndex].id, direction).finally(async () => {
        setGrabbedPost(undefined);
      });
    },
    [adjustedInViewPostIndex, mutablePosts, reorderPosts, setLoadingPosts, updatePostLocation]
  );

  // Keeps only the set number of posts in view with fall-off at end and/or start
  const postsToRender: InstagramGridPostType[] = useMemo(() => {
    return mutablePosts.slice(postIndexOffset.start, postIndexOffset.end);
  }, [mutablePosts, postIndexOffset]);

  const renderPost = useCallback(
    (item: InstagramGridPostType, idx: number) => (
      <div key={`item-${item.id}-${idx}`} className={classes.MediaCardWrapper}>
        <GridMediaCard
          index={idx}
          isMobile={isMobile}
          hasScrolling={hasScrollbar}
          post={item}
          movePost={movePost}
          cancelMove={cancelMove}
          endMove={moveEnd}
          isGrabbed={item.id === grabbedPost?.id}
          activeCategoryId={grabbedPost?.category?.id}
          movePostPosition={movePostPosition}
          trashSinglePost={trashSinglePost}
          reFetchPosts={props.getPostScheduleListItems}
        />
      </div>
    ),
    [
      isMobile,
      hasScrollbar,
      movePost,
      cancelMove,
      moveEnd,
      grabbedPost?.id,
      grabbedPost?.category?.id,
      movePostPosition,
      trashSinglePost,
      props.getPostScheduleListItems
    ]
  );

  const selectProfileHandler: FormElementAction<FormElementValueType> = useCallback(
    (value, complete) => {
      setMutablePosts([]);
      setSliderValue(undefined);
      profileSelectAction(String(value), complete);
    },
    [profileSelectAction, setSliderValue]
  );

  const pastPosts = igGridPosts.filter(p => p.status === PostStatus.PUBLISHED);
  // Slice the pastPosts array to render no more than the calculated number of past posts
  const pastPostsToRender = useMemo(() => {
    // Calculate the total number of future items to be rendered
    const totalFutureItems = sliderValue === 0 ? 0 : postsToRender.length + placeHolderCount;
    // Calculate the maximum number of past posts that can be rendered
    const maxPastPosts = Math.max(MAX_POSTS_IN_VIEW - totalFutureItems, 0);

    return pastPosts.slice(0, maxPastPosts);
  }, [pastPosts, placeHolderCount, postsToRender, sliderValue]);

  const valueTemplate: SelectValueTemplate = (props: SelectViewProps, globalClasses: string) => {
    const profile = instagramProfiles.find(profile => profile.id === props.selectedValue);

    return (
      <div
        className={classNames(globalClasses, classes.ProfileSelectValue)}
        onClick={() => {
          props.setExpanded(!props.expanded);
        }}>
        {profile ? (
          <SocialProfile
            socialProfile={{
              id: profile.id,
              provider: profilesHelpers.mapOAuthFromTypename(profile.__typename),
              network: profilesHelpers.mapNetworkFromTypename(profile.__typename),
              name: profile.displayName
            }}
            size={'small'}
            image={profile.photoUrl}
            label={true}
            disconnected={!profile.connected}
          />
        ) : (
          <span>Select a Profile</span>
        )}
      </div>
    );
  };
  const itemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    const selectProps: SelectProfileTemplateViewProps = {
      ...props,
      index,
      item,
      showDisplayName: true,
      iconSize: 'small',
      socialProfiles: instagramProfiles
    };

    return <SelectProfileItemTemplateView key={index} {...selectProps} />;
  };

  const renderErrorMessage = (error: string): JSX.Element | null => {
    const errorMap: Record<string, ErrorConfig | null> = {
      SOCIAL_ACCESS_TOKEN_INVALID: {
        message: 'We couldn’t fetch your past posts as the selected profile is disconnected.',
        label: 'Re-connect Profile',
        action: () => navigate({ pathname: '/settings/profiles' })
      },
      SOCIAL_PROFILE_INSTAGRAM_API_MISMATCH: null // Do not render any error
    };

    const defaultError = {
      message: 'We couldn’t fetch your past posts due to the Instagram error.',
      label: 'Try Again',
      action: () => props.refetchPosts()
    };

    const errorConfig = errorMap.hasOwnProperty(error) ? errorMap[error] : defaultError;

    if (!errorConfig) return null;

    const { message, label, action } = errorConfig;

    return (
      <div className={classNames('margin-top-small-8', classes.igErrorMessageBox)}>
        <div className={classNames(classes.Message, classes.MessageButton)}>{message}</div>
        <Button type="button" variant="hollow" size="medium" color={ButtonColorsEnum.alert} action={action}>
          {label}
        </Button>
      </div>
    );
  };

  // On first load, we should only show first 6 items... or all items if less than 6
  useEffect(() => {
    if (!loadingPosts && itemCnt && !sliderValue && sliderValue !== 0) {
      setSliderValue(itemCnt > 6 ? 6 : itemCnt);
    }
  }, [itemCnt, setSliderValue, sliderValue, loadingPosts]);

  useEffect(() => {
    setMutablePosts(() => igGridPosts.filter(p => p.status !== PostStatus.PUBLISHED));
  }, [igGridPosts]);

  const sliderContainerClasses = classNames('margin-top-small-16 margin-bottom-small-16');
  const sliderWrapperClasses = classNames('padding-top-small-24 padding-bottom-small-24', classes.Wrapper, {
    [classes.WrapperMobile]: isMobile
  });
  const scrollWrapperClasses = classNames('margin-top-small-8', classes.IGScrollWrapper, {
    [classes.IGScrollWrapperMobile]: isMobile
  });

  return (
    <>
      <CenteredContentLayout hasSidebar={false} loading={loadingPage}>
        {props.instagramProfiles.length === 0 ? (
          <div className={classes.NoProfileContainer}>
            <EmptyPlaceholder
              title={'You don’t have any Instagram Profiles'}
              image={instagramPreview}
              buttonTitle={'Connect an Instagram Profile'}
              buttonIcon={IconsEnum.plus}
              buttonAction={() => navigate({ pathname: '/settings/profiles' })}>
              <div>
                <p>
                  Plan your Instagram grid with ease! Use the slider to preview how it will evolve as new posts go live, and drag & drop posts to keep your grid looking perfect.<br />
                <a onClick={() => beacon('article', '702', { type: 'modal' })}>Learn more</a>
                </p>
              </div>
            </EmptyPlaceholder>
          </div>
        ) : (
          <div style={{ marginTop: '50px', marginBottom: '50px' }} className={classes.InstagramGrid}>
            <div className={sliderContainerClasses}>
              <div className={sliderWrapperClasses}>
                <Slider
                  max={itemCnt}
                  startingValue={sliderValue}
                  onChange={(value: number) => {
                    setSliderValue(value);
                  }}
                />
              </div>
            </div>

            <ConditionalWrapper isMobile={isMobile}>
              <div className={'grid-y'}>
                <div className={'margin-top-small-24'}>
                  <h5>
                    <PreviewDateString sliderValue={sliderValue ?? 0} posts={postsToRender} />
                  </h5>
                </div>
                <div className={'margin-top-small-16'}>
                  <Select
                    items={profileListItems}
                    name={'instagramSelect'}
                    rules={{}}
                    size={'large'}
                    useFormInstance={useFormInstance}
                    selectedValue={selectedProfileId}
                    action={selectProfileHandler}
                    valueTemplate={valueTemplate}
                    itemTemplate={itemTemplate}
                  />
                </div>
                {!isGraphApi && (
                  <div className={'margin-top-small-8'}>
                    <BoxNotification type={'warning'}>
                      For personal profiles, this grid is an estimate, because we can’t fetch data from the Instagram
                      API.
                    </BoxNotification>
                  </div>
                )}
                {props.igApiError && isGraphApi && <>{renderErrorMessage(props.igApiError)}</>}
                <div ref={scrollRef} className={scrollWrapperClasses}>
                  {props.igGridPosts.length ? (
                    <div className={classes.GridLayout}>
                      {[...Array(placeHolderCount)].map((x, i) => (
                        <div key={`item-placeholder-${x}-${i}`} className={classes.MediaCardWrapper}>
                          <GridMediaCard
                            index={i}
                            isMobile={isMobile}
                            hasScrolling={hasScrollbar}
                            isPlaceholder={true}
                            post={undefined}
                            movePost={movePost}
                            cancelMove={cancelMove}
                            endMove={moveEnd}
                            movePostPosition={movePostPosition}
                            trashSinglePost={trashSinglePost}
                            reFetchPosts={props.getPostScheduleListItems}
                          />
                        </div>
                      ))}
                      {sliderValue !== 0 && postsToRender.map(renderPost)}
                      {pastPostsToRender.map(renderPost)}
                    </div>
                  ) : (
                    <div className={classes.NoInstagramPostsWrapper}>
                      <EmptyPlaceholder
                        title={'Your Instagram Queue is empty'}
                        image={instagramPreview}
                        buttonTitle={'Add an Instagram Post'}
                        buttonIcon={IconsEnum.plus}
                        buttonAction={() => {
                          openNewPost({
                            source: PostSource.INSTAGRAM_GRID,
                            onClose: state.newPostCloseCallback,
                            selectedProfiles: [{ __typename: 'InstagramSocialProfile', profileId: selectedProfileId }]
                          });
                        }}>
                        <div><p>Plan your Instagram grid with ease! Use the slider to preview how it will evolve as new posts go live, and drag & drop posts to keep your grid looking perfect.<br />
                          <a onClick={() => beacon('article', '702', { type: 'modal' })}>Learn more</a>
                          </p>
                        </div>
                      </EmptyPlaceholder>
                    </div>
                  )}
                </div>
              </div>
            </ConditionalWrapper>
          </div>
        )}
      </CenteredContentLayout>
      <CardDragLayer scheduleItems={mutablePosts} isMobile={isMobile} />
    </>
  );
};
